/**
 * Route preloading strategies to improve perceived performance
 */

import { preloadComponents } from './lazy-loading';

/**
 * Preload critical routes that users are likely to visit
 */
export const preloadCriticalRoutes = async (): Promise<void> => {
  // Import functions for critical routes
  const criticalImports = [
    () => import(/* webpackChunkName: "core" */ '../pages/feed/FeedTabs'),
    () => import(/* webpackChunkName: "training" */ '../pages/training/UnifiedTrainingLogPage'),
    () => import(/* webpackChunkName: "profile" */ '../pages/profile/ProfilePage'),
  ];

  try {
    await preloadComponents(criticalImports);
  } catch (error) {
    // Failed to preload some critical routes
  }
};

/**
 * Preload routes based on user behavior patterns
 */
export const preloadByUserRole = async (userRole: 'admin' | 'instructor' | 'user'): Promise<void> => {
  let roleImports: Array<() => Promise<any>> = [];

  switch (userRole) {
    case 'admin':
      roleImports = [
        () => import(/* webpackChunkName: "admin" */ '../pages/admin/AdminDashboard'),
        () => import(/* webpackChunkName: "admin" */ '../pages/admin/UserManagementPage'),
      ];
      break;
    case 'instructor':
      roleImports = [
        () => import(/* webpackChunkName: "belt" */ '../pages/belt/BeltProgressionPage'),
        () => import(/* webpackChunkName: "training" */ '../pages/training/AddTrainingPage'),
      ];
      break;
    case 'user':
      roleImports = [
        () => import(/* webpackChunkName: "fitness" */ '../pages/goals/GoalsListPage'),
        () => import(/* webpackChunkName: "stats" */ '../pages/stats/TabTrainingLog'),
      ];
      break;
  }

  if (roleImports.length > 0) {
    try {
      await preloadComponents(roleImports);
    } catch (error) {
      // Failed to preload routes
    }
  }
};

/**
 * Preload routes when user hovers over navigation links
 */
export const preloadOnHover = (routeName: string): void => {
  const routeImports: Record<string, () => Promise<any>> = {
    training: () => import(/* webpackChunkName: "training" */ '../pages/training/UnifiedTrainingLogPage'),
    profile: () => import(/* webpackChunkName: "profile" */ '../pages/profile/ProfilePage'),
    goals: () => import(/* webpackChunkName: "fitness" */ '../pages/goals/GoalsListPage'),
    belt: () => import(/* webpackChunkName: "belt" */ '../pages/belt/BeltProgressionPage'),
    settings: () => import(/* webpackChunkName: "settings" */ '../pages/settings/SettingsPage'),
    ai: () => import(/* webpackChunkName: "ai" */ '../pages/ai/ChatPage'),
    premium: () => import(/* webpackChunkName: "premium" */ '../pages/premium/PremiumLandingPage'),
  };

  const importFn = routeImports[routeName];
  if (importFn) {
    importFn().catch((error) => {
      console.warn(`⚠️ Failed to preload ${routeName} route:`, error);
    });
  }
};

/**
 * Preload routes when the app becomes idle
 */
export const preloadOnIdle = (): void => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      preloadCriticalRoutes();
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(preloadCriticalRoutes, 100);
  }
};

/**
 * Initialize preloading strategy based on connection and device
 */
export const initializePreloading = (): void => {
  // Check if user is on a fast connection
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  const isSlowConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
  
  // Check if user prefers reduced data usage
  const saveData = connection && connection.saveData;

  if (!isSlowConnection && !saveData) {
    preloadOnIdle();
  }
};

/**
 * Navigation link preloading utility
 */
export const attachPreloadListeners = (): void => {
  // Add hover listeners to navigation links
  const navLinks = document.querySelectorAll('[data-preload]');
  
  navLinks.forEach((link) => {
    const routeName = link.getAttribute('data-preload');
    if (routeName) {
      link.addEventListener('mouseenter', () => preloadOnHover(routeName), { once: true });
      link.addEventListener('focus', () => preloadOnHover(routeName), { once: true });
    }
  });
};